import { StockHistoryModelType, StockOperationType } from '@teinor/erp/types/company/product/stockParent/stockHistory'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { productTypesInversed } from '@teinor/erp/types/company/product'
import { ButtonToOrder, CustomRenderProductStockHistory, CustomRenderStockOperationType } from '../../../../customRenderStock'
import { permissionCheck } from '../../../../../../baseComponents/PermissionChecker/function'
import { getUserPermissions } from '../../../../../../utils/reducers/getters'
import { CustomRenderDate, CustomRenderString } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { TwinDictionary } from '../../../../../../utils/globals/dictionary'
import { DisplayPriceGettingParams } from '../../../../../../baseComponents/Displays/DisplayPrice'
import { TwinSimpleTable } from '../../../../../../baseComponents/TwinTable'
import { ModalCreateEditStructureLayout } from '../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { handleStockIsNegativeTextColor, isStockNegative } from '../../../functions'
import { TableProductStockTy, TableTypeStock } from '../../../types'

interface TableProductStockHistorysProps extends TableProductStockTy {
    CurrencyId: string
}

const TableProductStockHistorys: React.FC<TableProductStockHistorysProps> = ({ productType, StockParentId, controlUnitType, unitMeasure, tableType, showSpecialCases, CurrencyId }) => {
    const { t } = useTwinTranslation()
    const columns: ColumnTableSchema<StockHistoryModelType> = [
        {
            id: 'createdAt',
            dataKey: 'createdAt',
            label: t('date', 'Fecha'),
            customRender: (parameterValue) => <CustomRenderDate value={parameterValue} />,
            width: 110
        },
        {
            id: 'operationFullAssignedNumber',
            dataKey: 'operationFullAssignedNumber',
            label: t('ref', 'Ref'),
            width: 85,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue || allRowData.operationId} />
        },
        {
            id: 'operationType',
            dataKey: 'operationType',
            label: t('operation', 'Operación'),
            customRender: (parameterValue) => <CustomRenderStockOperationType value={parameterValue as StockOperationType} />,
        },
    ]
    const onlyUnits = controlUnitType === 0
    const onlyMeasure = controlUnitType === 1
    const bothStocks = controlUnitType === 2

    if (onlyUnits || bothStocks) {
        columns.push({
            id: 'operationStock',
            dataKey: 'operationStock',
            label: t('operationStock', 'Movimiento'),
            customRender: (parameterValue) => <CustomRenderString className={handleStockIsNegativeTextColor(parameterValue)} value={isStockNegative(parameterValue) ? parameterValue : '+' + parameterValue} />,
        })
        columns.push({
            id: 'currentStock',
            dataKey: 'currentStock',
            label: t('stock', 'Inventario'),
        })
        columns.push({
            id: 'unitCost',
            dataKey: 'unitCost',
            label: t('unitCost', 'Coste unitario'),
            customRender: (parameterValue) => <DisplayPriceGettingParams value={parameterValue ? parseFloat(parameterValue) : 0} CurrencyId={CurrencyId} with_vat={false} />,
            havePermission: permissionCheck(getUserPermissions('warehouse.stock.costs'), 'read'),
        })
    }
    if (onlyMeasure || bothStocks) {
        columns.push({
            id: 'operationMeasureStock',
            dataKey: 'operationStock',
            label: t('operationMeasureStock', 'Movimiento Medida'),
            customRender: (parameterValue) => <CustomRenderString className={handleStockIsNegativeTextColor(parameterValue)} value={isStockNegative(parameterValue) ? parameterValue + ' ' + unitMeasure : '+' + parameterValue + ' ' + unitMeasure} />,
        })
        columns.push({
            id: 'currentMeasureStock',
            dataKey: 'currentStock',
            label: t('MeasureStock', 'Inventario Medida'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue + ' ' + unitMeasure} />,
        })
        columns.push({
            id: 'measureCost',
            dataKey: 'unitCost',
            label: t('measureCost', 'Coste Medida'),
            customRender: (parameterValue) => <DisplayPriceGettingParams value={parameterValue ? parseFloat(parameterValue) : 0} CurrencyId={CurrencyId} with_vat={false} />,
            havePermission: permissionCheck(getUserPermissions('warehouse.stock.costs'), 'read'),
        })
    }
    if (showSpecialCases) {
        if (tableType === 'individual') {
            columns.push({
                id: 'product',
                dataKey: 'MirrorStockChildId',
                label: t('product', 'Producto'),
                searchKey: 'name',
                customRender: (parameterValue, allRowData) => <CustomRenderProductStockHistory name={allRowData?.MirrorStockChild?.Product?.ProductLangs?.[0]?.name} unique_number={allRowData?.MirrorStockChild?.Product?.unique_number} id={String(allRowData?.MirrorStockChildId)} />,
                searchableComponent: {
                    component: 'SearchableComponentBasic',
                    extraComponentData: { label: t('product', 'Producto') }
                },
            })
        } else {
            columns.push({
                id: 'productGrouped',
                dataKey: 'StockParentId',
                label: t('product', 'Producto'),
                searchKey: productType === productTypesInversed['lots'] || productType === productTypesInversed['serialized'] ? 'unique_number' : 'name',
                customRender: (parameterValue, allRowData) => <CustomRenderProductStockHistory name={allRowData?.StockParent?.Product?.ProductLangs?.[0]?.name} unique_number={allRowData?.StockParent?.Product?.unique_number} id={String(allRowData?.id)} />,
                searchableComponent: {
                    component: 'SearchableComponentBasic',
                    extraComponentData: { label: t('product', 'Producto') }
                },
            })
        }
    }

    columns.push({
        id: 'operationId',
        dataKey: 'operationId',
        label: '',
        customRender: (parameterValue, allRowData) => <ButtonToOrder id={parameterValue} operationType={allRowData?.operationType} />,
        width: 50
    })
    const tableProps: Record<TableTypeStock, { getDataFrom: string, name: string, extraWhereParams: TwinDictionary }> = {
        'individual': { getDataFrom: '/api/app/product/stock/getLastStockHistoriesOfStockParentId', name: 'productStockHistories', extraWhereParams: { StockParentId } },
        'groupByVariants': { getDataFrom: '/api/app/product/stock/getLastStockHistoriesOfStockParentIdGroupByVariants', name: 'productStockHistoriesGroupByVariants', extraWhereParams: { StockParentId } },
        'groupByUniqueNumber': { getDataFrom: '/api/app/product/stock/getLastStockHistoriesOfStockParentIdGroupByUniqueNumber', name: 'productStockHistoriesGroupByUniqueNumber', extraWhereParams: { StockParentId } },
    }
    return (
        <ModalCreateEditStructureLayout loading={false} translations={{
            title: t('lastMovementStock', 'Últimos movimientos de stock'),
            button: ''
        }} haveButtonPermissions={false} className='flex-col' >
            <TwinSimpleTable columns={columns} {...tableProps[tableType]} />
        </ModalCreateEditStructureLayout>
    )
}


export default TableProductStockHistorys